import { useRef, useState } from 'react';

import { ImageCompressionService } from '../services/ImageCompressionService';

export const useCamera = () => {
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const startCamera = async () => {
    console.log('Starting camera...');
    try {
      let mediaStream;

      const advancedConstraints = {
        video: {
          facingMode: 'environment',
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 },
          frameRate: { ideal: 30, min: 15 },
          focusMode: 'continuous',
          autoGainControl: true,
          noiseSuppression: true,
          torch: false, // Start with torch off
        },
      };

      const basicConstraints = {
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };

      try {
        console.log('Trying advanced camera constraints...');
        mediaStream = await navigator.mediaDevices.getUserMedia(advancedConstraints);
      } catch (advancedError) {
        console.log('Advanced constraints failed, trying basic rear camera...', advancedError);
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia(basicConstraints);
        } catch (rearError) {
          console.log('Rear camera not available, trying default camera...');
          mediaStream = await navigator.mediaDevices.getUserMedia({
            video: true,
          });
        }
      }

      console.log('Media stream obtained:', mediaStream);

      const videoTracks = mediaStream.getVideoTracks();
      if (videoTracks.length > 0) {
        const capabilities = videoTracks[0].getCapabilities();
        const settings = videoTracks[0].getSettings();
        console.log('Camera capabilities:', capabilities);
        console.log('Current camera settings:', settings);
      }

      setStream(mediaStream);
      setShowCamera(true);

      setTimeout(() => {
        if (videoRef.current) {
          console.log('Setting video source...');
          videoRef.current.srcObject = mediaStream;
          videoRef.current.onloadedmetadata = () => {
            console.log('Video metadata loaded, playing...');
            videoRef.current.play().catch(e => console.error('Error playing video:', e));
          };
        } else {
          console.error('Video ref not available');
        }
      }, 100);
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert(`Unable to access camera: ${error.message}. Please try uploading from gallery instead.`);
    }
  };

  const captureImage = async onImageCaptured => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      async blob => {
        try {
          console.log('[IMAGE] Compressing camera capture...');
          const compressedBlob = await ImageCompressionService.compressCameraCapture(blob);

          if (onImageCaptured) {
            onImageCaptured(compressedBlob);
          }
          stopCamera();
        } catch (error) {
          console.error('Camera capture compression failed:', error);
          if (onImageCaptured) {
            onImageCaptured(blob);
          }
          stopCamera();
        }
      },
      'image/jpeg',
      0.9
    );
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  return {
    showCamera,
    stream,
    videoRef,
    canvasRef,
    startCamera,
    captureImage,
    stopCamera,
  };
};
